import { formatPrice } from '@/common/constant';
import type { WineRecommendation } from '@/common/generated/wine-dish-recommendation.typebox';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { Card } from '@/pages/live-chat/components/card';
import { Tag } from 'antd';
import { ExternalLink } from 'lucide-react';
import { PairingDetailsCard } from './pairing-details-card';
interface WineRecommendationCardProps {
  title?: string;
  recommendation: WineRecommendation;
  displayType: 'primary' | 'alternative';
  className?: string;
}

export function WineRecommendationCard({
  title,
  recommendation,
  displayType,
  className = '',
}: WineRecommendationCardProps) {
  const { currency } = useCurrencySymbol();

  return (
    <>
      <Card
        className={`flex flex-col gap-4 p-4 relative ${displayType === 'primary' ? 'border-2 border-primary/30 shadow-md' : ''} ${className}`}
      >
        {displayType === 'primary' && (
          <div className="absolute -top-3 left-4 bg-violet-100 text-violet-700 px-2 py-0.5 text-sm rounded-full font-medium">
            Primary Recommendation
          </div>
        )}
        {title && (
          <div className="absolute -top-3 left-4 bg-blue-100 text-blue-700 px-2 py-0.5 text-sm rounded-full font-medium">
            {title}
          </div>
        )}
        <div className="flex gap-4">
          {recommendation.productImageUrl && (
            <img
              src={recommendation.productImageUrl}
              alt={recommendation.name}
              className="w-24 h-32 object-cover rounded-md"
            />
          )}
          <div className="flex-1">
            {recommendation.productUrl ? (
              <a
                href={recommendation.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-lg font-semibold hover:text-primary"
              >
                {recommendation.name}
                <ExternalLink className="h-4 w-4" />
              </a>
            ) : (
              <h3 className="text-lg font-semibold">{recommendation.name}</h3>
            )}
            <p className="text-sm text-muted-foreground">
              {recommendation.description ||
                recommendation.pairingRationale.matchRationale}
            </p>
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold">
                  Price Range: {recommendation.priceRange}
                </p>
                <span className="text-muted-foreground">•</span>
                <p className="font-semibold">
                  Price: {formatPrice(recommendation.price, currency)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">
                  Pairing Score: {recommendation.totalPairingScore}
                </p>
                <span className="text-muted-foreground">•</span>
                <p className="text-sm text-muted-foreground">
                  Confidence: {getConfidenceLevel(recommendation.confidence)}
                </p>
              </div>
              <p className="text-sm">
                {recommendation.type} • {recommendation.varietal} •{' '}
                {recommendation.region}
              </p>
              <p className="text-sm">
                Body: {recommendation.characteristics.body} • Acidity:{' '}
                {recommendation.characteristics.acidity} • Sweetness:{' '}
                {recommendation.characteristics.sweetness}
                {recommendation.characteristics.tannins &&
                  ` • Tannins: ${recommendation.characteristics.tannins}`}
              </p>
              <div className="mt-3 text-sm">
                <p>
                  <span className="font-medium">Aging:</span>{' '}
                  {recommendation.agingPotential}
                </p>
                <p>
                  <span className="font-medium">Serving Temp:</span>{' '}
                  {recommendation.servingTemp.min}°C -{' '}
                  {recommendation.servingTemp.max}°C
                </p>
              </div>

              {/* tags */}
              <div className="flex flex-wrap gap-2 text-sm">
                {[
                  {
                    label: recommendation.sourceId,
                    value: recommendation.sourceName,
                  },
                  {
                    label: recommendation.supplierId,
                    value: recommendation.supplierName,
                  },
                ].map((attr) =>
                  attr.label ? (
                    <Tag
                      color="#18181b1a"
                      className="!text-black"
                      bordered={false}
                      key={attr.label}
                    >
                      {attr.value}
                    </Tag>
                  ) : null,
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
      <PairingDetailsCard
        className={
          displayType === 'primary'
            ? 'border-2 border-primary/30 shadow-md'
            : ''
        }
        rationale={recommendation.pairingRationale}
      />
    </>
  );
}

// Helper function to convert confidence number to text level
function getConfidenceLevel(confidence: number): string {
  if (confidence >= 0.8) return 'high';
  if (confidence >= 0.5) return 'medium';
  return 'low';
}
